import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

export default function PaymentReturnPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("loading");
  const { t } = useLanguage();

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {
        /* réessaie */
      }
      tries += 1;
      if (tries < 8) setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
  }, [sessionId]);

  return (
    <main data-testid="payment-return-page" className="mx-auto flex max-w-lg flex-col items-center px-4 py-24 text-center">
      <Seo title={t("Paiement — GoSpot")} siteName="GoSpot" description={t("Confirmation de ton paiement GoSpot.")} />
      {state === "loading" && (
        <>
          <Loader2 className="h-14 w-14 animate-spin text-[#2563EB]" />
          <h1 className="mt-6 text-2xl font-extrabold text-slate-900">{t("Confirmation du paiement…")}</h1>
          <p className="mt-2 text-sm text-slate-500">{t("Un instant, on vérifie avec Stripe.")}</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="h-14 w-14 text-emerald-500" />
          <h1 className="mt-6 text-2xl font-extrabold text-slate-900">{t("Paiement confirmé !")}</h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">
            {t("Ta place est réservée et ton reçu part par courriel. Retrouve tout dans ton profil.")}
          </p>
          <div className="mt-8 flex gap-3">
            <Link to="/profil" data-testid="payment-goto-profil" className="rounded-full bg-[#2563EB] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#1D4ED8]">
              {t("Voir mes réservations")}
            </Link>
            <Link to="/" className="rounded-full border border-slate-200 px-6 py-3 text-sm font-semibold text-slate-600 transition hover:border-blue-300">
              {t("Accueil")}
            </Link>
          </div>
        </>
      )}
      {(state === "pending" || state === "error") && (
        <>
          <XCircle className="h-14 w-14 text-amber-500" />
          <h1 className="mt-6 text-2xl font-extrabold text-slate-900">
            {state === "pending" ? t("Paiement en cours de traitement") : t("Session introuvable")}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">
            {state === "pending"
              ? t("Stripe prend un peu plus de temps que prévu. Ta réservation apparaîtra dans ton profil dès confirmation.")
              : t("Impossible de retrouver cette session de paiement.")}
          </p>
          <Link to="/profil" data-testid="payment-goto-profil" className="mt-8 rounded-full bg-[#2563EB] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#1D4ED8]">
            {t("Voir mon profil")}
          </Link>
        </>
      )}
    </main>
  );
}
